import moment from 'moment'

import { SERVICE_ID_TO_API_PLAN } from '../ManagePlan/constants'
import { getSuggestedEngageStylePlanName } from '../ManagePlan/suggestedCustomPlanName'
import { directfulServiceNames } from './PrepayPreferences/EditPaymentPeriodDialog/constants'

const API_PLAN_TO_SERVICE_ID = Object.fromEntries(
  Object.entries(SERVICE_ID_TO_API_PLAN).map(([serviceId, apiPlan]) => [apiPlan, serviceId])
)

export const ENGAGE_BUNDLE_PLANS = new Set([
  'DirectfulCore',
  'Unlimited',
  'Basic',
  'Messaging',
  'Email',
  'CartAbandonment'
])

const ENGAGE_ANCHOR_PLAN_ORDER = [
  'DirectfulCore',
  'Unlimited',
  'Basic',
  'Messaging',
  'Email',
  'CartAbandonment'
]

/** Subscriptions shown in Plan & Billing (matches PrepayPreferences + SubscriptionsRow). */
export function getVisibleSubscriptions(subscriptions) {
  return (subscriptions || []).filter(s => {
    if (s.startDate === s.endDate) {
      return false
    }
    if (s.endDate && moment(s.endDate).isBefore(moment())) {
      return false
    }
    return true
  })
}

export function subscriptionsToEngageServiceIds(subscriptions) {
  const visible = getVisibleSubscriptions(subscriptions)
  const serviceIds = new Set()
  for (const sub of visible) {
    if (!ENGAGE_BUNDLE_PLANS.has(sub.plan)) {
      continue
    }
    const serviceId = API_PLAN_TO_SERVICE_ID[sub.plan]
    if (serviceId) {
      serviceIds.add(serviceId)
    }
  }
  if (visible.some(s => s.plan === 'DirectfulCore') && !serviceIds.has('core_sms')) {
    serviceIds.add('core_sms')
  }
  return serviceIds
}

export function getEngageStylePlanNameFromSubscriptions(subscriptions) {
  return getSuggestedEngageStylePlanName(subscriptionsToEngageServiceIds(subscriptions))
}

function trimPackageName(value) {
  if (value == null) {
    return ''
  }
  return String(value).trim()
}

/** Package name from API when set on any active engage-bundle subscription. */
export function getStoredPackageName(subscriptions) {
  const visible = getVisibleSubscriptions(subscriptions)
  for (const plan of ENGAGE_ANCHOR_PLAN_ORDER) {
    const sub = visible.find(s => s.plan === plan)
    const name = trimPackageName(sub?.packageName)
    if (name) {
      return name
    }
  }
  return ''
}

function hasActiveUnlimitedPlan(subscriptions) {
  return getVisibleSubscriptions(subscriptions).some(s => s.plan === 'Unlimited')
}

function hasDirectfulCorePlanRate(subscriptions) {
  return getVisibleSubscriptions(subscriptions).some(
    s => s.plan === 'DirectfulCore' && (s.planRate ?? 0) > 0
  )
}

export function getEngageAnchorPlan(subscriptions) {
  const visible = getVisibleSubscriptions(subscriptions).filter(s =>
    ENGAGE_BUNDLE_PLANS.has(s.plan)
  )
  if (!visible.length) {
    return null
  }
  if (hasActiveUnlimitedPlan(subscriptions) && !hasDirectfulCorePlanRate(subscriptions)) {
    return 'Unlimited'
  }
  for (const plan of ENGAGE_ANCHOR_PLAN_ORDER) {
    if (visible.some(s => s.plan === plan)) {
      return plan
    }
  }
  return visible[0].plan
}

/** Whether this subscription row should appear in Plan & Billing (bundle rows collapse to one). */
export function shouldShowSubscriptionRow(subscription, allSubscriptions) {
  if (!ENGAGE_BUNDLE_PLANS.has(subscription.plan)) {
    return true
  }
  if (
    subscription.plan === 'DirectfulCore' &&
    hasActiveUnlimitedPlan(allSubscriptions) &&
    !hasDirectfulCorePlanRate(allSubscriptions)
  ) {
    return false
  }
  if (
    hasActiveUnlimitedPlan(allSubscriptions) &&
    hasDirectfulCorePlanRate(allSubscriptions)
  ) {
    return subscription.plan === 'DirectfulCore' || subscription.plan === 'Unlimited'
  }
  const anchor = getEngageAnchorPlan(allSubscriptions)
  return anchor === subscription.plan
}

export function getSubscriptionRowDisplayName(subscription, allSubscriptions) {
  const { plan, packageName } = subscription
  if (ENGAGE_BUNDLE_PLANS.has(plan)) {
    const stored =
      trimPackageName(packageName) || getStoredPackageName(allSubscriptions)
    if (stored) {
      return stored
    }
    return getEngageStylePlanNameFromSubscriptions(allSubscriptions)
  }
  return directfulServiceNames?.[plan] || plan
}
